import React from 'react';
import PropTypes from 'prop-types';

import { Grid, Stack, Typography } from '@mui/material';

import { useDispatch } from 'react-redux';
import { appStateActions } from 'store/appStateSlice';
import { useConsult } from 'shared/context/ConsultContext';
import { useFormState } from 'shared/hooks';
import useConsultData from 'features/consults/hooks/useConsultData';
import useConsultInitialFormState from 'features/consults/hooks/useConsultInitialFormState';

import { Box } from 'styles/layout';
import GridWrapper from 'shared/components/layout/GridWrapper/GridWrapper';
import { CONSULT_TABS } from 'features/consults/utils/constants';
import ConsultIcd from 'features/consults/components/ConsultIcd';
import ConsultLock from 'features/consults/components/ConsultLock/ConsultLock';
import Autocomplete from 'shared/components/forms/Autocomplete';
import QuestionsPanel from 'features/consults/components/tabFields/QuestionsPanel';
import formFields from 'features/consults/shared/tabs/Signature/formFields';
import {
  eegIcds,
  pedsEegIcds,
} from 'features/consults/shared/tabs/Signature/data';
import { ServiceTypeEnum } from 'utils/enums';

import api from 'api';
import { SnackbarTypeEnum } from 'shared/components/feedback/Snackbar/SnackbarTypes.enum';
import { isCptCodeMultiSelect } from 'features/consults/utils/validators';
import usePermission from 'shared/hooks/usePermission';
import { PermissionEnum } from 'utils/permissions';
import CheckboxField from 'shared/components/forms/Checkbox/CheckboxField';
import QualityReviewPanel from './QualityReviewPanel/QualityReviewPanel';

import signatureFields from './data';
import getSignatureValidationSchema from './validationSchema';

const SignatureEEG = ({ errors }) => {
  const dispatch = useDispatch();

  const { consult, consultFacility } = useConsultData(CONSULT_TABS.SIGNATURE);

  const schema = getSignatureValidationSchema(consult, consultFacility);

  const {
    clearErrors,
    control,
    register,
    getValues,
    getFieldError,
    setFormValues,
    setValue,
    trigger,
    watch,
  } = useFormState(schema, { stateSlice: 'consults', showAsSnackbar: true });
  const {
    consultTabData,
    isClosed,
    isEditable,
    isMacro,
    handleConsultLock,
    handleFieldChange,
    handleOwnerChange,
    saveConsultChange,
  } = useConsultData(CONSULT_TABS.SIGNATURE, getValues);

  const { consultProviders } = useConsult();
  const hasPaocPermission = usePermission(PermissionEnum.HasPaocPermission);

  const currentAttestationValue = watch(
    formFields.attestationOfEEGCompletion.name,
  );

  useConsultInitialFormState(
    consult.id,
    {
      ...consultTabData,
      eegcptCodes: Array.isArray(consultTabData.eegcptCodes)
        ? consultTabData.eegcptCodes
        : [],
    },
    setFormValues,
  );

  const [totalDuration, setTotalDuration] = React.useState(
    consult?.eeg?.totalDuration || null,
  );

  React.useEffect(() => {
    const fetchTotalDuration = async () => {
      try {
        const { data } = await api.getConsultTotalDuration({
          id: consult.id,
          type: consult.type,
        });
        if (data?.totalDuration) {
          setTotalDuration(data.totalDuration);
        }
      } catch (error) {
        dispatch(
          appStateActions.showSnackbar({
            text:
              error?.response?.data?.title ?? 'Failed to fetch total duration.',
            type: SnackbarTypeEnum.WARNING,
            duration: 5000,
          }),
        );
      }
    };
    if (
      consult?.demographics?.studyId &&
      consult?.demographics?.observationType === 'DisconnectedEndClip' &&
      !consult?.eeg?.totalDuration &&
      consult?.id
    ) {
      fetchTotalDuration();
    }
  }, [
    consult?.id,
    consult?.type,
    consult?.demographics?.studyId,
    consult?.demographics?.observationType,
    consult?.eeg?.totalDuration,
    dispatch,
  ]);

  React.useEffect(() => {
    clearErrors();
    if (errors) trigger(errors);
  }, [errors, clearErrors, trigger]);

  return (
    <GridWrapper>
      <Grid item sm={12} lg={6}>
        <QuestionsPanel
          control={control}
          register={register}
          onChange={handleFieldChange}
          getFieldError={getFieldError}
          disabled={!isEditable}
          elements={signatureFields}
          disableElements={
            isMacro
              ? [
                  formFields.attestationOfEEGCompletion.name,
                  formFields.eegTelemedicine.name,
                  formFields.providerLocationPredefined.name,
                  formFields.providerLocationFreeText.name,
                  formFields.patientLocation.name,
                  ...(consult.macroType === 'Shared'
                    ? [formFields.eegcptCodes.name]
                    : []),
                ]
              : []
          }
        />
        {consult?.demographics?.observationType === 'DisconnectedEndClip' &&
          totalDuration && (
            <Box mb={2}>
              <Stack spacing={1} direction="row">
                <Typography fontSize={16} variant="subtitle">
                  Total Duration:
                </Typography>
                <Stack direction="row" spacing={5}>
                  {totalDuration}
                </Stack>
              </Stack>
            </Box>
          )}
        <ConsultIcd
          consultId={consult.id}
          consultData={consultTabData}
          staticOptions={
            consult.type === ServiceTypeEnum.EEG ? eegIcds : pedsEegIcds
          }
          disabled={!isEditable}
          onSetValue={setValue}
          getFieldError={getFieldError}
          isMacro={isMacro}
          maxCodes={isCptCodeMultiSelect(consult, consultFacility) ? 2 : 1}
        />
      </Grid>
      <Grid item sm={12} lg={6}>
        <Box mb={1}>
          <Autocomplete
            control={control}
            name="assignToId"
            label="Neurologist"
            options={consultProviders}
            required
            disabled={isMacro || isClosed}
            handleChange={handleOwnerChange}
          />
        </Box>
        <Box mb={1}>
          <Autocomplete
            control={control}
            name="backupOnCallProviderId"
            label="Are you providing backup for on-call provider? If so select on-call below:"
            options={consultProviders}
            disabled={isClosed || (isMacro && consult.macroType === 'Shared')}
            handleChange={() => handleFieldChange('backupOnCallProviderId')}
          />
        </Box>
        {!isMacro && (
          <ConsultLock
            consult={consult}
            attestationValue={currentAttestationValue}
            disabled={!isEditable}
            checked={isClosed}
            onConsultLock={handleConsultLock}
          />
        )}
        <QualityReviewPanel
          form={{
            control,
            register,
            watch,
            getValues,
            setFormValues,
            getFieldError,
          }}
          withOptions
          onChange={saveConsultChange}
          onFieldChange={handleFieldChange}
          disabled={isMacro || !isEditable}
        />
        {hasPaocPermission && (
          <CheckboxField
            control={control}
            name={formFields.completedByPaoc.name}
            label={formFields.completedByPaoc.label}
            disabled={isMacro || !isEditable}
            onChange={() => handleFieldChange(formFields.completedByPaoc.name)}
          />
        )}
      </Grid>
    </GridWrapper>
  );
};

SignatureEEG.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
};

export default SignatureEEG;
